/**
 * Four ways to drive the same tank.
 *
 * Keyboard and mouse are the desk: keys steer the hull, the pointer aims the
 * turret, and the two are independent, which is the whole appeal of a plan
 * view. A gamepad is the same thing with sticks.
 *
 * A phone is a different game if you make it hold a steering wheel, so touch
 * gets floating twin sticks and a different *model*: the left thumb points
 * where the tank should go and the hull turns to face it, the right thumb
 * points the gun and fires by itself once it is pushed far enough. Nobody has
 * a third thumb for a fire button.
 *
 * Everything lands in one `Intent`, which the game reads once a frame and
 * never has to ask where it came from.
 */

export type Vec = { x: number; y: number };

export type Intent = {
  /** -1 reverse to 1 full ahead. */
  throttle: number;
  /** -1 left to 1 right, for the keyboard's tank steering. */
  steer: number;
  /** Touch and gamepad instead say where to point; the hull turns to it. */
  turnTo: Vec | null;
  /** A direction for the turret, from a stick. */
  aimDir: Vec | null;
  /** A point on the surface for the turret, from a mouse. */
  pointer: Vec | null;
  fire: boolean;
  boost: boolean;
};

type Stick = {
  id: number;
  originX: number;
  originY: number;
  x: number;
  y: number;
  el: HTMLElement | null;
  knob: HTMLElement | null;
};

const RADIUS = 52;

const clampStick = (dx: number, dy: number) => {
  const len = Math.hypot(dx, dy);
  if (len <= RADIUS) return { x: dx / RADIUS, y: dy / RADIUS, mag: len / RADIUS };
  return { x: dx / len, y: dy / len, mag: 1 };
};

export class Input {
  readonly intent: Intent = {
    throttle: 0,
    steer: 0,
    turnTo: null,
    aimDir: null,
    pointer: null,
    fire: false,
    boost: false,
  };

  /** True once anything has touched the surface; the sticks appear then. */
  touching = false;
  /** While false, keys are read but nothing is swallowed from the page. */
  capture = false;

  private keys = new Set<string>();
  private mouseDown = false;
  private left: Stick | null = null;
  private right: Stick | null = null;
  private padPause = false;
  private cleanup: Array<() => void> = [];

  constructor(
    private surface: HTMLElement,
    private opts: { onPause: () => void; onTouch?: () => void },
  ) {
    const on = <K extends keyof DocumentEventMap>(
      target: EventTarget,
      type: K | string,
      handler: (event: never) => void,
      options?: AddEventListenerOptions,
    ) => {
      target.addEventListener(type, handler as EventListener, options);
      this.cleanup.push(() => target.removeEventListener(type, handler as EventListener, options));
    };

    on(document, "keydown", this.onKeyDown);
    on(document, "keyup", this.onKeyUp);
    on(window, "blur", this.reset);
    on(surface, "pointerdown", this.onPointerDown);
    on(surface, "pointermove", this.onPointerMove);
    on(surface, "pointerup", this.onPointerUp);
    on(surface, "pointercancel", this.onPointerUp);
    on(surface, "pointerleave", this.onPointerLeave);
    on(surface, "contextmenu", (event: Event) => event.preventDefault());
  }

  // ---- keyboard ------------------------------------------------------------

  private onKeyDown = (event: KeyboardEvent) => {
    // Any key the game uses is the game's: claim it, or the window passes it
    // up the chain and macOS answers with a beep for every W and every space.
    const key = event.key.toLowerCase();
    const ours = GAME_KEYS.has(key) && !event.metaKey && !event.ctrlKey && !event.altKey;
    const onControl = (event.target as Element | null)?.closest?.("input, textarea, select, [contenteditable]");
    if (ours && !onControl && !(key === " " && (event.target as Element | null)?.closest?.("button"))) {
      event.preventDefault();
    }
    if (event.repeat) return;
    if (key === "escape" || key === "p") {
      this.opts.onPause();
      return;
    }
    this.keys.add(key);
  };

  private onKeyUp = (event: KeyboardEvent) => {
    this.keys.delete(event.key.toLowerCase());
  };

  // ---- pointer -------------------------------------------------------------

  private isTouch = (event: PointerEvent) => event.pointerType !== "mouse";

  private local = (event: PointerEvent): Vec => {
    const rect = this.surface.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  /** Menus and the pause button are DOM, and a thumb on one is not a stick. */
  private onChrome = (event: PointerEvent): boolean =>
    !!(event.target as Element | null)?.closest?.("button, a, input, [data-act]");

  private onPointerDown = (event: PointerEvent) => {
    if (!this.isTouch(event)) {
      if (this.onChrome(event)) return;
      this.mouseDown = true;
      this.intent.pointer = this.local(event);
      return;
    }

    if (!this.touching) {
      this.touching = true;
      this.opts.onTouch?.();
    }
    // Off the clock, on a title screen or a pause menu, every touch belongs to
    // the buttons. Capturing the pointer here would swallow the tap and leave
    // a phone with a game it cannot start.
    if (!this.capture || this.onChrome(event)) return;
    const p = this.local(event);
    const side = p.x < this.surface.clientWidth / 2 ? "left" : "right";
    const existing = side === "left" ? this.left : this.right;
    if (existing) return;

    const el = this.surface.querySelector<HTMLElement>(`[data-stick="${side}"]`);
    const stick: Stick = {
      id: event.pointerId,
      originX: p.x,
      originY: p.y,
      x: 0,
      y: 0,
      el,
      knob: el?.querySelector<HTMLElement>("[data-stick-knob]") ?? null,
    };
    if (side === "left") this.left = stick;
    else this.right = stick;
    this.paint(stick, true);
    try {
      this.surface.setPointerCapture(event.pointerId);
    } catch {
      // Some pointers cannot be captured. The stick still tracks by id.
    }
  };

  private onPointerMove = (event: PointerEvent) => {
    if (!this.isTouch(event)) {
      this.intent.pointer = this.local(event);
      return;
    }
    const stick = this.stickFor(event.pointerId);
    if (!stick) return;
    const p = this.local(event);
    const v = clampStick(p.x - stick.originX, p.y - stick.originY);
    stick.x = v.x;
    stick.y = v.y;
    this.paint(stick, true);
  };

  private onPointerUp = (event: PointerEvent) => {
    if (!this.isTouch(event)) {
      this.mouseDown = false;
      return;
    }
    const stick = this.stickFor(event.pointerId);
    if (!stick) return;
    this.paint(stick, false);
    if (this.left === stick) this.left = null;
    if (this.right === stick) this.right = null;
  };

  private onPointerLeave = (event: PointerEvent) => {
    if (!this.isTouch(event)) this.intent.pointer = null;
  };

  private stickFor = (id: number): Stick | null =>
    this.left?.id === id ? this.left : this.right?.id === id ? this.right : null;

  /** The stick is DOM, so it can be styled with the rest of the game's chrome. */
  private paint(stick: Stick, on: boolean): void {
    if (!stick.el) return;
    if (on) {
      stick.el.style.left = `${stick.originX}px`;
      stick.el.style.top = `${stick.originY}px`;
      stick.el.dataset.on = "";
      if (stick.knob) {
        stick.knob.style.transform = `translate(-50%, -50%) translate(${stick.x * RADIUS}px, ${stick.y * RADIUS}px)`;
      }
    } else {
      delete stick.el.dataset.on;
      if (stick.knob) stick.knob.style.transform = "translate(-50%, -50%)";
    }
  }

  // ---- once a frame --------------------------------------------------------

  update(): void {
    const i = this.intent;
    const held = (...names: string[]) => names.some((n) => this.keys.has(n));

    let throttle = 0;
    let steer = 0;
    if (held("w", "arrowup")) throttle += 1;
    if (held("s", "arrowdown")) throttle -= 1;
    if (held("a", "arrowleft")) steer -= 1;
    if (held("d", "arrowright")) steer += 1;

    i.boost = held("shift");
    i.fire = this.mouseDown || held(" ", "enter", "f");
    i.turnTo = null;
    i.aimDir = null;

    // Touch overrides the keyboard, because a thumb on a stick is a
    // deliberate act and a stuck key is not.
    if (this.left) {
      const mag = Math.hypot(this.left.x, this.left.y);
      if (mag > 0.18) {
        i.turnTo = { x: this.left.x / mag, y: this.left.y / mag };
        throttle = Math.min(1, mag);
        steer = 0;
      } else {
        throttle = 0;
      }
    }
    if (this.right) {
      const mag = Math.hypot(this.right.x, this.right.y);
      if (mag > 0.2) {
        i.aimDir = { x: this.right.x / mag, y: this.right.y / mag };
        if (mag > 0.55) i.fire = true;
      }
    }

    this.pollPad(i);

    i.throttle = Math.max(-1, Math.min(1, throttle));
    i.steer = Math.max(-1, Math.min(1, steer));
  }

  private pollPad(i: Intent): void {
    const pads = navigator.getGamepads?.() ?? [];
    const pad = [...pads].find((p) => p && p.connected);
    if (!pad) {
      this.padPause = false;
      return;
    }
    const dead = (v: number | undefined) => (Math.abs(v ?? 0) < 0.22 ? 0 : (v ?? 0));
    const lx = dead(pad.axes[0]);
    const ly = dead(pad.axes[1]);
    const rx = dead(pad.axes[2]);
    const ry = dead(pad.axes[3]);

    const leftMag = Math.hypot(lx, ly);
    if (leftMag > 0.22) {
      i.turnTo = { x: lx / leftMag, y: ly / leftMag };
      i.throttle = Math.min(1, leftMag);
      i.steer = 0;
    }
    const rightMag = Math.hypot(rx, ry);
    if (rightMag > 0.25) i.aimDir = { x: rx / rightMag, y: ry / rightMag };

    const pressed = (n: number) => pad.buttons[n]?.pressed === true;
    if (pressed(7) || pressed(0) || pressed(5)) i.fire = true;
    if (pressed(6) || pressed(1)) i.boost = true;

    const start = pressed(9);
    if (start && !this.padPause) this.opts.onPause();
    this.padPause = start;
  }

  /** Let go of everything: on blur, on pause, on a state change. */
  reset = (): void => {
    this.keys.clear();
    this.mouseDown = false;
    if (this.left) this.paint(this.left, false);
    if (this.right) this.paint(this.right, false);
    this.left = null;
    this.right = null;
    this.intent.fire = false;
    this.intent.throttle = 0;
    this.intent.steer = 0;
    this.intent.turnTo = null;
    this.intent.aimDir = null;
  };

  dispose(): void {
    this.reset();
    this.cleanup.forEach((off) => off());
    this.cleanup = [];
  }
}

/** Every key the game answers to, lower-cased as `event.key` gives them. */
const GAME_KEYS = new Set([
  "w", "a", "s", "d", "f", "p", " ", "shift", "enter", "escape",
  "arrowup", "arrowdown", "arrowleft", "arrowright",
]);
